/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { CriticRatingSource, PreviewType, RecordingStatus } from 'client-code/previews';
import { NdvrInteractor } from 'src/core/interactors';

import {
    COLLECTION_SCREEN_PREVIEW_DESCRIPTION_ONE_LINE_LENGTH,
    ONE_LINE_PREVIEW_DESCRIPTION,
    PREVIEW_CAST_MAX_LENGTH,
    PREVIEW_DESCRIPTION_ONE_LINE_LENGTH,
    PREVIEW_TITLE_MAX_LENGTH,
    PREVIEW_TITLE_ONE_LINE_LENGTH,
    PREVIEW_TITLE_SIZE_REDUCTION_LENGTH,
    TIVO_SERVICES,
    TWO_LINES_PREVIEW_DESCRIPTION,
} from '../../config';
import { AppUtils } from '../../core/AppUtils';
import { DeviceModel } from '../../core/device/DeviceModel';
import { ServicesClientImpl } from '../../core/interactors/ServicesClientImpl';
import { Locale } from '../../core/locale/Locale';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import { TOfferPreviewCustomData } from '../../core/types/TOfferPreviewCustomData';
import { DateTime } from '../../core/utils';
import { ThuzzRatingTypes } from './ThuzzRatingTypes';
import { DimensionScaler } from '../../core/device/DimensionScaler';

export class OfferPreviewFormatter {
    private services = new ServicesClientImpl();

    private EMPTY_SPACE = '\u2009';

    private LEFT_QUOTE = '\u2018';

    private RIGHT_QUOTE = '\u2019';

    private LONG_DASH = '\u2013';

    private mOfferPreview: TOfferPreviewCustomData = {};

    private mContentPreview: any = {};

    private mCcVideoResolutionText: string = '';

    private mPreviewCastText: string = '';

    private mPreviewTitleText: string = '';

    private mPreviewDescriptionText: string = '';

    private mCategoryAndFirstAiredText: string = '';

    private mIsAdult: boolean | undefined;

    private mInternalRatings: [] = [];

    /**
     * ----- Remark -----
     * I tried creating the isOfType custom function to figure out what type of preview
     * we’re dealing with, so the data can be formatted accordingly. But, turns out
     * TypeScript interfaces and types are like invisible friends – they only exist at
     * compile time and vanish at runtime. So, I can't just hand TOfferPreviewCustomData
     * over as an argument.
     * If anyone has a clever way to make this work, let's chat and maybe we can give this
     * design a makeover!
     */

    public constructor(offerPreview: TOfferPreviewCustomData, shouldHideMetadata?: boolean) {
        this.mOfferPreview = offerPreview;
        this.mContentPreview = offerPreview?.contentPreview || offerPreview;
        this.mIsAdult =
            offerPreview?.contentPreview?.isAdult &&
            (shouldHideMetadata || this.services.shouldHideMetadata());
        this._formatPreviewCast();
        this._formatPreviewTitle();
        this._formatPreviewDescription();
        this._formatCcVideoResolution();
        this._formatCategoryAndFirstAired();
        this._storeInternalRatings();
    }

    private _formatCategoryAndFirstAired = (): void => {
        const categoryText = this.mContentPreview?.categoryLabel
            ? this.mContentPreview.categoryLabel.split(',').splice(0, 2).join(',')
            : '';

        const firstAiredFormattedText = this.getFormattedFirstAired();

        this.mCategoryAndFirstAiredText = [categoryText, firstAiredFormattedText]
            .filter((value) => value.length > 0)
            .join(', ');
    };

    private _formatCcVideoResolution = (): void => {
        const ccText = this.mOfferPreview?.hasCc ? 'CC' : '';
        const videoResolutionText = this.mOfferPreview?.videoResolution?.toUpperCase() || '';
        const ccVideoResolutionText = [ccText, videoResolutionText]
            .filter((value) => value.length > 0)
            .join(', ');

        this.mCcVideoResolutionText =
            ccVideoResolutionText.length > 0 ? `${this.EMPTY_SPACE}(${ccVideoResolutionText})` : '';
    };

    private _formatPreviewDescription = (): void => {
        const seriesText = AppUtils.generateSeasonEpisodeText(
            this.mContentPreview?.seasonNumber,
            this.mContentPreview?.episodeNumber,
        );
        const episodeTitleText =
            (this.mContentPreview?.episodeTitle &&
                ` ${this.LEFT_QUOTE}${this.mContentPreview?.episodeTitle}${this.RIGHT_QUOTE} ${this.LONG_DASH} `) ||
            '';
        const description = this.mContentPreview?.description || '';

        this.mPreviewDescriptionText = `${seriesText}${episodeTitleText}${description}`;
    };

    private _formatPreviewCast = (): void => {
        const { creditString, cast } = this.mContentPreview || {};
        const updatedCreditString =
            creditString
                ?.split('~')
                .map((role) => role.split(':')[1]?.trim())
                .filter(Boolean)
                .join(', ') || '';
        const combinedCast = (cast?.join(', ') || updatedCreditString)
            .split(',')
            .map((name) => name.trim())
            .filter(Boolean)
            .join(', ');
        this.mPreviewCastText = combinedCast;
    };

    private _formatPreviewTitle = (): void => {
        if (this.mIsAdult) this.mPreviewTitleText = Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
        else
            this.mPreviewTitleText = this.mContentPreview?.title
                ? `${this.mContentPreview.title}${this.mContentPreview.postfixTitle ? ` (${this.mContentPreview.postfixTitle})` : ''}`
                : '';
    };

    private _populateCastWithActors = (creditString: string): string | undefined => {
        if (!creditString) return undefined;
        const roles = creditString.split('~');
        const actorRole = roles.find((role) => role.startsWith('actor:'));
        if (actorRole) {
            const actors = actorRole.replace('actor:', '').split(',');
            return actors.join(', ');
        }
        return undefined;
    };

    private _storeInternalRatings = (): void => {
        if (!this.mIsAdult)
            this.mInternalRatings =
                this.mContentPreview?.internalRatingList
                    ?.map(({ typeId, valueId }) => {
                        return this.services.getInternalRatingName(`${typeId}${valueId}`);
                    })
                    .filter((ratingName: string) => ratingName.length > 0) || [];
    };

    public getCategoryAndFirstAired = (): string => {
        return this.mIsAdult ? '' : `${this.EMPTY_SPACE}${this.mCategoryAndFirstAiredText}`;
    };

    public getCcVideoResolution = (): string => {
        return this.mIsAdult ? '' : this.mCcVideoResolutionText;
    };

    public getContentAiringDateTime = (): string => {
        return !this.mIsAdult
            ? AppUtils.generateDateTimeText(
                  this.mContentPreview?.startTime,
                  this.mContentPreview?.endTime,
                  this.mContentPreview?.duration,
                  DeviceModel.getTVProperties().getLang(),
              )
            : '';
    };

    public getDurationTime = (): string => {
        // convert to milliseconds
        return this.mContentPreview?.duration != null
            ? DateTime.convertToReadable(this.mContentPreview?.duration * 1000)
            : '';
    };

    public getRecordingDurationTime = (): string => {
        return DateTime.convertToReadable(
            (this.mContentPreview?.recordingDuration ?? (this.mContentPreview?.duration || 0)) *
                1000,
        );
    };

    public getChannelLogoUrl = (width: number, height: number): string => {
        const channelLogoIndex = this.mContentPreview?.channel?.logoIndex;
        if (!channelLogoIndex) return '';

        const serviceBaseUrls = new NdvrInteractor().getServicesBaseUrls();
        const channelLogoBaseUrl = serviceBaseUrls[TIVO_SERVICES.ChannelLogoImageHost];
        const { height: scaledHeight, width: scaledWidth } =
            DimensionScaler.getInstance().getScaledDimensions(width, height);
        const channelLogoUrl = `${channelLogoBaseUrl}/logos/${scaledWidth}x${scaledHeight}/${channelLogoIndex - 65536}.png`;

        return channelLogoUrl;
    };

    public getEpisodeTitle = (): string => {
        let seriesText = '';
        let episodeTitleText = '';

        if (this.mContentPreview.previewType === PreviewType.EpisodePreview) {
            seriesText = AppUtils.generateSeasonEpisodeText(
                this.mContentPreview?.seasonNumber,
                this.mContentPreview?.episodeNumber,
            );
            episodeTitleText =
                (this.mContentPreview?.episodeTitle &&
                    `${this.LEFT_QUOTE}${this.mContentPreview?.episodeTitle}${this.RIGHT_QUOTE}`) ||
                '';
        } else if (this.mContentPreview.previewType === PreviewType.SportEventPreview) {
            episodeTitleText =
                (this.mContentPreview?.sportTitle &&
                    `${this.LEFT_QUOTE}${this.mContentPreview?.sportTitle}${this.RIGHT_QUOTE}`) ||
                '';
        }
        // trimming in case seriesText is empty
        return `${seriesText} ${episodeTitleText}`.trim();
    };

    public getFormattedCast = (): string => {
        const sanitizedCreditString =
            this.mContentPreview.creditString?.replace(/anchor:\s*/gi, '') || '';

        return (
            this.mContentPreview.cast?.join(', ') ||
            sanitizedCreditString ||
            this._populateCastWithActors(this.mContentPreview.creditString)
        );
    };

    public getParsedCast = (): string => {
        const descriptionLineCount =
            this.mPreviewDescriptionText.length >
            COLLECTION_SCREEN_PREVIEW_DESCRIPTION_ONE_LINE_LENGTH
                ? 2
                : 1;
        if (descriptionLineCount === 1 && this.hasPreviewCastTruncation()) {
            return `${this.mPreviewCastText?.substring(0, 2 * PREVIEW_CAST_MAX_LENGTH)}`;
        }
        if (descriptionLineCount === 2 && this.hasPreviewCastTruncation()) {
            return `${this.mPreviewCastText?.substring(0, PREVIEW_CAST_MAX_LENGTH)}`;
        }
        return this.mPreviewCastText;
    };

    public getFormattedFirstAired = (): string => {
        const firstAiredDate = this.mContentPreview?.firstAiredDate
            ? new Date(this.mContentPreview.firstAiredDate)
            : null;

        const currentTime = new Date();
        const isFutureAiring = firstAiredDate && firstAiredDate.getTime() > currentTime.getTime();

        const firstAiredText = isFutureAiring
            ? Locale.getString(LocaleKeys.FIRST_AIRS)
            : Locale.getString(LocaleKeys.FIRST_AIRED);

        const firstAiredFormattedText = firstAiredDate
            ? AppUtils.strftime(firstAiredDate, `${firstAiredText} %M/%D/%y`)
            : '';

        return firstAiredFormattedText;
    };

    public getGenres = (): string => {
        return this.mContentPreview?.genres?.join(', ') || this.mContentPreview?.categoryLabel;
    };

    public getInternalRatings = (): [] => {
        return this.mInternalRatings;
    };

    public getMovieYear = (): string => {
        if (this.mIsAdult) return '';
        return this.mContentPreview.movieYear ? `(${this.mContentPreview.movieYear})` : '';
    };

    public getPreviewDescription = (): string => {
        return this.mIsAdult
            ? Locale.getString(LocaleKeys.GUIDE_DESCRIPTION_HIDDEN)
            : this.mPreviewDescriptionText;
    };

    public getPreviewDescriptionTruncated = (): string => {
        return this.mPreviewDescriptionText.slice(
            0,
            2 * COLLECTION_SCREEN_PREVIEW_DESCRIPTION_ONE_LINE_LENGTH,
        );
    };

    public getPreviewDescriptionClasses = (): string => {
        return `preview-description${
            this.getPreviewTitle().length > PREVIEW_DESCRIPTION_ONE_LINE_LENGTH
                ? ' reduce-height'
                : ''
        }`;
    };

    public getPreviewTitle = (): string => {
        return this.mPreviewTitleText;
    };

    public getPreviewTitleClasses = (): string => {
        return `preview-title${
            this.mPreviewTitleText.length > PREVIEW_TITLE_SIZE_REDUCTION_LENGTH
                ? ' adjust-font'
                : ''
        }`;
    };

    public getProvidersImageUrls = (): string[] | undefined => {
        return this.mOfferPreview?.contentPreview?.providersImageUrls;
    };

    public getRecordingStatusMessage = (): { type: string; message: string } | null => {
        if (this.mContentPreview?.recordingStatus === RecordingStatus.InProgressRecording) {
            return {
                type: RecordingStatus.InProgressRecording,
                message: `This ${this.hasMoviePreview() ? 'movie' : 'episode'} is currently recording`,
            };
        }
        if (this.mContentPreview?.isPartialRecording) {
            const endTime = this.mContentPreview?.endTime;
            const duration = this.mContentPreview?.recordingDuration;

            if (endTime && duration) {
                const timeRange = DateTime.createTimeRange({ endTime, duration });
                const readableDuration = DateTime.convertToReadable(duration * 1000);

                return {
                    type: 'partialRecording',
                    message: `Partial recording ${timeRange} | ${readableDuration}`,
                };
            }
        }

        return null;
    };

    public getStarRating = (): number => {
        return this.mContentPreview?.starRating || 0;
    };

    public getStreamType = (): string => {
        return this.mContentPreview?.streamType || '';
    };

    public getThuzzRating = (): { type: string; value: number } | null => {
        // TODO : Check for other types of Sports preview and append here
        if (
            this.mContentPreview?.previewType === PreviewType.SportEventPreview &&
            this.mContentPreview?.recordingStatus !== RecordingStatus.InProgressRecording &&
            this.mContentPreview?.criticRating?.source === CriticRatingSource.Thuuz &&
            this.mContentPreview?.criticRating?.value != null
        ) {
            let type: string;
            const value: number = this.mContentPreview?.criticRating?.value;

            if (value >= 0 && value <= 39) {
                type = ThuzzRatingTypes.DULL_GAME;
            } else if (value <= 64) {
                type = ThuzzRatingTypes.OK_GAME;
            } else if (value <= 84) {
                type = ThuzzRatingTypes.GOOD_GAME;
            } else {
                type = ThuzzRatingTypes.GREAT_GAME;
            }
            return { type, value };
        }

        return null;
    };

    public getOnePassRightPreviewTitle = (): string => {
        return `OnePass for ${this.getPreviewTitle()}`;
    };

    public hasMoviePreview = (): boolean => {
        const previewType: string = this.mContentPreview?.previewType;
        return previewType === PreviewType.MoviePreview;
    };

    public hasNewIcon = (): boolean => {
        if (this.mIsAdult) return false;
        return this.mContentPreview?.isNew;
    };

    public hasPreviewDescriptionTruncation = (): boolean => {
        const numberOfDiscriptionLines =
            this.getPreviewTitle().length > PREVIEW_TITLE_ONE_LINE_LENGTH
                ? ONE_LINE_PREVIEW_DESCRIPTION
                : TWO_LINES_PREVIEW_DESCRIPTION;
        const descriptionMaxLength = PREVIEW_DESCRIPTION_ONE_LINE_LENGTH * numberOfDiscriptionLines;
        const descriptionLength =
            this.mPreviewDescriptionText.length +
            this.mCcVideoResolutionText.length +
            this.mCategoryAndFirstAiredText.length;

        return descriptionLength > descriptionMaxLength;
    };

    public hasPreviewCastDivTruncation = (): boolean => {
        return (
            this.mPreviewDescriptionText.length >
            2 * COLLECTION_SCREEN_PREVIEW_DESCRIPTION_ONE_LINE_LENGTH
        );
    };

    public hasPreviewCastTruncation = (): boolean => {
        return this.mPreviewCastText.length > PREVIEW_CAST_MAX_LENGTH;
    };

    public hasPreviewTitleTruncation = (): boolean => {
        return this.mPreviewTitleText.length > PREVIEW_TITLE_MAX_LENGTH;
    };

    public hasStarRating = (): boolean => {
        return !this.mIsAdult ? this.mContentPreview?.starRating !== undefined : false;
    };

    public isAdult = (): boolean => {
        // !! is to consider isAdult as false if undefined
        return !!this.mIsAdult;
    };

    public isAvailableInSocu = (): boolean => {
        return !!this.mOfferPreview?.isAvailableInSocu;
    };
}
